import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jobsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Briefcase, MapPin, Clock, DollarSign, Building, ExternalLink } from 'lucide-react';

export const JobDetailPage = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await jobsAPI.getById(id);
        setJob(res.data);
      } catch (err) {
        console.error('Error fetching job:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  if (loading) {
    return (
      <div className="container-wide py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-12 bg-muted rounded w-1/2" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container-wide py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Job not found</h1>
        <Link to="/jobs">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="job-detail-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <Link to="/jobs" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Jobs
          </Link>
          
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-sm bg-muted flex items-center justify-center">
              <Building className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="badge-category">{job.job_type}</span>
                <span className="text-sm text-muted-foreground capitalize">{job.experience_level} level</span>
              </div>
              <h1 className="text-display text-3xl lg:text-4xl mb-2">{job.title}</h1>
              <p className="text-xl text-muted-foreground">{job.company}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section>
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div>
                <h2 className="text-heading text-xl mb-4">Job Description</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">{job.description}</p>
              </div>

              {/* Tags */}
              {job.tags && job.tags.length > 0 && (
                <div>
                  <h2 className="text-heading text-xl mb-4">Required Skills</h2>
                  <div className="flex flex-wrap gap-2">
                    {job.tags.map((tag, i) => (
                      <span key={i} className="badge-category">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold">{job.location}</div>
                      {job.is_remote && <div className="text-xs text-muted-foreground">Remote available</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold capitalize">{job.job_type}</div>
                      <div className="text-xs text-muted-foreground">Job Type</div>
                    </div>
                  </div>
                  {job.salary_range && (
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{job.salary_range}</div>
                        <div className="text-xs text-muted-foreground">Salary Range</div>
                      </div>
                    </div>
                  )}
                  {job.created_at && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{new Date(job.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-muted-foreground">Posted</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {job.apply_url ? (
                <a href={job.apply_url} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full" size="lg">
                    Apply Now
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              ) : (
                <Button className="w-full" size="lg">
                  Apply Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
